import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { useIsAuthenticated } from '@azure/msal-react'
import { authApi } from '@/features/auth/api/authApi'
import { ApiError } from '@/shared/api/client'
import { AUTHZ_KEY } from '@/shared/api/queryClient'
import { msalInstance } from '@/features/auth/lib/msalConfig'
import {
  isLiveSyncAvailable,
  markLiveSyncUnavailable,
  normalizeRoleStatusUser,
} from '@/features/auth/hooks/useAuthz'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { showToast } from '@/shared/components/ui/Toast'
import type { RoleStatusUser, UserPermissions } from '@/features/auth/types'

function envInt(value: unknown, fallback: number): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

/** Poll cadence while the tab is visible and the user is actively interacting. */
const ACTIVE_MS = envInt(import.meta.env.VITE_ROLE_SYNC_ACTIVE_MS, 10_000)

/** Backoff once the user has gone quiet — still catches changes, at lower cost. */
const IDLE_MS = envInt(import.meta.env.VITE_ROLE_SYNC_IDLE_MS, 60_000)

/** No pointer/keyboard/scroll activity for this long counts as idle. */
const IDLE_AFTER_MS = 2 * 60_000

/** Floor between network checks, so bursts of events can't stampede the API. */
const MIN_GAP_MS = 3_000

/**
 * Fallback mode only. Auth_GetMyProfile writes a USER_LOGGED_IN audit row on every
 * call, so it is revalidated at most this often — event-driven, never on a timer.
 */
const FALLBACK_MIN_GAP_MS = 5 * 60_000

/** Same-origin channel used to fan a fresh snapshot out to sibling tabs. */
const CHANNEL_NAME = 'ir-portal:authz'

const DISABLED_LOGOUT_DELAY_MS = 1_500

const PERMISSION_KEYS: (keyof UserPermissions)[] = [
  'canUploadFiles',
  'canGenerateStatements',
  'canDraftEmails',
  'canSendEmails',
  'canViewReports',
  'canManageUsers',
  'canDeleteBatches',
]

interface AuthzDiff {
  roleChanged: boolean
  revoked: (keyof UserPermissions)[]
  granted: (keyof UserPermissions)[]
}

interface BroadcastPayload {
  /** MSAL account the snapshot belongs to — guards against cross-account bleed. */
  homeAccountId: string
  user: RoleStatusUser
}

/**
 * Diff two authorization snapshots client-side.
 *
 * The server's own `changes` payload is backed by an in-memory Map local to each
 * Function instance, so it silently reports "no changes" after a cold start or
 * scale-out. `currentUser` is always a direct SQL read, so we diff that instead.
 */
function diffAuthz(prev: RoleStatusUser | undefined, next: RoleStatusUser): AuthzDiff {
  if (!prev) return { roleChanged: false, revoked: [], granted: [] }

  return {
    roleChanged: prev.role !== next.role,
    revoked: PERMISSION_KEYS.filter((k) => prev.permissions[k] && !next.permissions[k]),
    granted: PERMISSION_KEYS.filter((k) => !prev.permissions[k] && next.permissions[k]),
  }
}

function currentHomeAccountId(): string | null {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  return account?.homeAccountId ?? null
}

/**
 * Keeps the live authorization state in sync with the back-end.
 *
 * Mount exactly once, above the authenticated route tree. Writing to AUTHZ_KEY
 * re-renders every `useAuthz()` subscriber in the same commit, so gated routes
 * and components mount/unmount without a refresh or re-login.
 *
 * Checks are driven by an adaptive timer plus navigation, focus, visibility and
 * reconnect events, and results are shared across tabs over a BroadcastChannel.
 */
export function useRoleSync() {
  const queryClient = useQueryClient()
  const isAuthenticated = useIsAuthenticated()
  const { logout } = useAuth()
  const location = useLocation()

  const logoutRef = useRef(logout)
  logoutRef.current = logout

  const inFlight = useRef(false)
  const loggingOut = useRef(false)
  const lastCheckAt = useRef(0)
  const lastActivityAt = useRef(Date.now())
  const checkRef = useRef<(force?: boolean) => void>(() => {})

  useEffect(() => {
    if (!isAuthenticated) return

    let cancelled = false
    let timer: number | undefined

    const channel =
      typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null

    const applySnapshot = (next: RoleStatusUser, broadcast: boolean) => {
      const prev = queryClient.getQueryData<RoleStatusUser>(AUTHZ_KEY)
      queryClient.setQueryData(AUTHZ_KEY, next)

      const { roleChanged, revoked, granted } = diffAuthz(prev, next)
      const changed =
        roleChanged || revoked.length > 0 || granted.length > 0 || prev?.isActive !== next.isActive

      // Only gossip real transitions; a steady-state poll shouldn't wake other tabs.
      if (broadcast && changed && channel) {
        const homeAccountId = currentHomeAccountId()
        if (homeAccountId) {
          channel.postMessage({ homeAccountId, user: next } satisfies BroadcastPayload)
        }
      }

      if (!next.isActive) {
        if (loggingOut.current) return
        loggingOut.current = true
        showToast('error', 'Account disabled', 'Your access has been revoked. Signing out…')
        window.setTimeout(() => logoutRef.current(), DISABLED_LOGOUT_DELAY_MS)
        return
      }

      if (revoked.length > 0) {
        // Drop cached server data the user may no longer be allowed to read.
        queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' })
        showToast('error', 'Access reduced', 'Your permissions were updated.')
      } else if (granted.length > 0 || roleChanged) {
        queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'auth' })
        showToast('success', 'Access updated', 'New features are now available.')
      }
    }

    const check = async (force = false) => {
      if (cancelled || loggingOut.current || inFlight.current) return
      if (document.visibilityState !== 'visible') return

      const now = Date.now()
      const minGap = isLiveSyncAvailable() ? MIN_GAP_MS : FALLBACK_MIN_GAP_MS
      if (!force && now - lastCheckAt.current < minGap) return

      // Fallback mode: no dedicated endpoint, so revalidate through the query
      // (Auth_GetMyProfile) sparingly rather than polling it.
      if (!isLiveSyncAvailable()) {
        lastCheckAt.current = now
        queryClient.invalidateQueries({ queryKey: AUTHZ_KEY })
        return
      }

      inFlight.current = true
      lastCheckAt.current = now
      try {
        const res = await authApi.checkRoleStatus()
        if (cancelled) return
        applySnapshot(normalizeRoleStatusUser(res.currentUser), true)
      } catch (err) {
        // A missing route means live sync is unavailable in this environment;
        // stop polling instead of retrying a 404 forever.
        if (err instanceof ApiError && err.status === 404) markLiveSyncUnavailable()
        // Any other failure is transient — keep the last known state and retry.
        // useAuthz() stays fail-closed if the query itself errors out.
      } finally {
        inFlight.current = false
      }
    }

    checkRef.current = (force) => void check(force)

    const schedule = () => {
      window.clearTimeout(timer)
      const idle = Date.now() - lastActivityAt.current > IDLE_AFTER_MS
      const delay = isLiveSyncAvailable() ? (idle ? IDLE_MS : ACTIVE_MS) : IDLE_MS

      timer = window.setTimeout(async () => {
        await check()
        if (!cancelled) schedule()
      }, delay)
    }

    const onActivity = () => {
      lastActivityAt.current = Date.now()
    }

    const onWake = () => {
      if (document.visibilityState !== 'visible') return
      lastActivityAt.current = Date.now()
      void check()
    }

    // useAuthz()'s own query has just fetched on mount, so treat that as the first
    // check rather than issuing a duplicate request here.
    lastCheckAt.current = Date.now()
    schedule()

    const activityEvents: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'scroll']
    activityEvents.forEach((e) => window.addEventListener(e, onActivity, { passive: true }))
    document.addEventListener('visibilitychange', onWake)
    window.addEventListener('focus', onWake)
    window.addEventListener('online', onWake)

    if (channel) {
      channel.onmessage = (event: MessageEvent<BroadcastPayload>) => {
        const payload = event.data
        if (!payload?.user) return
        // Tabs can hold different accounts (MSAL caches in sessionStorage), so
        // only accept a snapshot minted for the account this tab is signed in as.
        if (payload.homeAccountId !== currentHomeAccountId()) return
        applySnapshot(normalizeRoleStatusUser(payload.user), false)
      }
    }

    return () => {
      cancelled = true
      window.clearTimeout(timer)
      activityEvents.forEach((e) => window.removeEventListener(e, onActivity))
      document.removeEventListener('visibilitychange', onWake)
      window.removeEventListener('focus', onWake)
      window.removeEventListener('online', onWake)
      channel?.close()
    }
  }, [isAuthenticated, queryClient])

  // Re-check on navigation: entering a gated route is exactly when a stale
  // permission would be most visible.
  useEffect(() => {
    if (!isAuthenticated) return
    checkRef.current()
  }, [location.pathname, isAuthenticated])
}
